import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";
import type { KeyboardEvent } from "react";

import { api } from "../../api";
import { SortableTable } from "../../components/SortableTable";
import { date, timeAgo } from "../../format";
import { Alert, Card, Stat } from "../../shell/ui";
import { AlertList, HealthDot, bytes, useOpsAlerts } from "./opsShared";
import type { Health } from "./opsShared";

/**
 * Admin → Market data: what the hub hosts for everyone, and the place to add a
 * market. A market is a symbol on a venue; each of its timeframes is synced
 * hourly by the scheduler and listed for members once it has bars.
 */

type MarketType = "spot" | "futures" | "equity" | "forex";

interface Dataset {
  symbol: string;
  timeframe: string;
  marketType: MarketType;
  historyDays: number;
  defaultHistoryDays: number | null;
  enabled: boolean;
  lastSyncAt: string | null;
  lastSyncMs: number | null;
  lastError: string | null;
  createdAt: string;
  addedBy: string | null;
  candleCount: number;
  minTime: number | null;
  maxTime: number | null;
  stale: boolean;
  botsRunning: number;
  botsTotal: number;
}

interface DataJobRow {
  id: string;
  name: string;
  data: { kind: string; symbol?: string; timeframe?: string; marketType?: string };
  progress: number | { done: number; total: number };
  state: "active" | "waiting" | "delayed" | "failed" | "completed";
  addedAt: number;
  startedAt: number | null;
  finishedAt: number | null;
  error: string | null;
  synced: number | null;
  failed: number | null;
}

interface DatasetsResponse {
  items: Dataset[];
  candleBytes: number | null;
  jobs: DataJobRow[];
  schedules: Array<{ name: string; pattern: string | null; next: number | null }>;
  timeframes: string[];
  defaults: Record<string, number>;
}

interface Market {
  key: string;
  symbol: string;
  marketType: MarketType;
  rows: Dataset[];
}

const MARKET_LABEL: Record<MarketType, string> = {
  spot: "Spot",
  futures: "Futures",
  equity: "Stocks & ETFs",
  forex: "FX",
};

const ALL_TFS = ["1m", "3m", "5m", "15m", "30m", "1h", "2h", "4h", "6h", "8h", "12h", "1d", "3d", "1w"];
const PRESETS: Array<{ label: string; tfs: string[] }> = [
  { label: "Swing", tfs: ["15m", "1h", "4h", "1d"] },
  { label: "Intraday", tfs: ["1m", "5m", "15m", "1h"] },
  { label: "Daily", tfs: ["1d"] },
  { label: "Everything", tfs: ALL_TFS },
];
const tfOrder = (tf: string) => ALL_TFS.indexOf(tf);

function healthOf(d: Dataset): Health {
  if (!d.enabled) return "off";
  if (d.lastError) return "error";
  if (d.stale || d.candleCount === 0) return "warn";
  return "ok";
}

function worst(rows: Dataset[]): Health {
  const hs = rows.map(healthOf);
  if (hs.includes("error")) return "error";
  if (hs.includes("warn")) return "warn";
  if (hs.every((h) => h === "off")) return "off";
  return "ok";
}

const compact = (n: number) =>
  n >= 1e9
    ? `${(n / 1e9).toFixed(1)}B`
    : n >= 1e6
      ? `${(n / 1e6).toFixed(1)}M`
      : n >= 1e3
        ? `${(n / 1e3).toFixed(0)}k`
        : String(n);

function useDatasets() {
  return useQuery({
    queryKey: ["admin", "datasets"],
    queryFn: () => api<DatasetsResponse>("/admin/datasets"),
    // Fast while something syncs, so a market just added fills in as you watch.
    refetchInterval: (q) =>
      q.state.data?.jobs.some((j) => j.state === "active" || j.state === "waiting") ? 3_000 : 20_000,
  });
}

export function MarketDataTab() {
  const q = useDatasets();
  const alerts = useOpsAlerts();
  const [open, setOpen] = useState<string | null>(null);
  const [type, setType] = useState<MarketType | "all">("all");
  const [show, setShow] = useState<"all" | "problems" | "paused">("all");
  const [search, setSearch] = useState("");
  const detailRef = useRef<HTMLDivElement>(null);

  const markets = useMemo(() => {
    const m = new Map<string, Market>();
    for (const d of q.data?.items ?? []) {
      const key = `${d.marketType}|${d.symbol}`;
      const cur = m.get(key) ?? { key, symbol: d.symbol, marketType: d.marketType, rows: [] };
      cur.rows.push(d);
      m.set(key, cur);
    }
    for (const mk of m.values()) mk.rows.sort((a, b) => tfOrder(a.timeframe) - tfOrder(b.timeframe));
    return [...m.values()];
  }, [q.data]);

  const syncing = new Set(
    (q.data?.jobs ?? [])
      .filter((j) => (j.state === "active" || j.state === "waiting") && j.data.kind === "sync")
      .map((j) => `${j.data.marketType}|${j.data.symbol}|${j.data.timeframe}`),
  );
  const syncingAll = (q.data?.jobs ?? []).some(
    (j) => (j.state === "active" || j.state === "waiting") && j.data.kind === "sync-all",
  );

  const needle = search.trim().toUpperCase();
  const visible = markets.filter((m) => {
    if (type !== "all" && m.marketType !== type) return false;
    const h = worst(m.rows);
    if (show === "problems" && (h === "ok" || h === "off")) return false;
    if (show === "paused" && !m.rows.some((r) => !r.enabled)) return false;
    return !needle || m.symbol.includes(needle);
  });

  const items = q.data?.items ?? [];
  const dataAlerts = (alerts.data?.items ?? []).filter((a) => a.area === "market-data");
  const lastSync = Math.max(0, ...items.map((d) => (d.lastSyncAt ? new Date(d.lastSyncAt).getTime() : 0)));
  const next = q.data?.schedules.find((s) => s.name === "sync-all")?.next;
  const selected = open ? markets.find((m) => m.key === open) : undefined;

  useEffect(() => {
    if (open) detailRef.current?.scrollIntoView({ block: "nearest", behavior: "smooth" });
  }, [open]);

  return (
    <div className="stack">
      <Card title="Needs attention">
        <AlertList
          items={dataAlerts}
          loading={alerts.isPending}
          onDataset={(d) => setOpen(`${d.marketType}|${d.symbol}`)}
        />
      </Card>
      <Card title="Hosted market data">
        <div className="stat-grid stat-grid--pairs">
          <Stat label="Markets" value={q.data ? markets.length : "—"} hint="A symbol on one venue" />
          <Stat
            label="Datasets"
            value={
              q.data
                ? `${items.filter((d) => d.enabled).length} on · ${items.filter((d) => !d.enabled).length} paused`
                : "—"
            }
            hint="A market on one timeframe"
          />
          <Stat
            label="Candles"
            value={q.data ? compact(items.reduce((s, d) => s + d.candleCount, 0)) : "—"}
          />
          <Stat
            label="Storage"
            value={bytes(q.data?.candleBytes)}
            hint="The candles table, compressed chunks included"
          />
          <Stat label="Last sync" value={lastSync ? timeAgo(lastSync) : "—"} />
          <Stat
            label="Next sync"
            value={
              syncingAll
                ? "running now"
                : next
                  ? new Date(next).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
                  : "—"
            }
          />
        </div>
      </Card>

      <AddMarkets data={q.data} />

      {selected && (
        <div ref={detailRef}>
          <MarketDetail market={selected} syncing={syncing} onClose={() => setOpen(null)} />
        </div>
      )}

      <Card
        title={`Markets · ${visible.length}`}
        actions={
          <>
            <input
              className="filter"
              type="search"
              aria-label="Search markets"
              placeholder="Symbol"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <select
              aria-label="Venue"
              value={type}
              onChange={(e) => setType(e.target.value as MarketType | "all")}
            >
              <option value="all">All venues</option>
              {(Object.keys(MARKET_LABEL) as MarketType[]).map((t) => (
                <option key={t} value={t}>
                  {MARKET_LABEL[t]}
                </option>
              ))}
            </select>
            <div className="segmented" role="group" aria-label="Show">
              {(["all", "problems", "paused"] as const).map((s) => (
                <button
                  key={s}
                  type="button"
                  className={show === s ? "is-active" : undefined}
                  aria-pressed={show === s}
                  onClick={() => setShow(s)}
                >
                  {s[0]!.toUpperCase() + s.slice(1)}
                </button>
              ))}
            </div>
          </>
        }
        flush
      >
        <SortableTable
          rows={visible}
          loading={q.isPending}
          rowKey={(m) => m.key}
          caption="Hosted markets"
          empty={<p className="card__body muted">No markets match.</p>}
          columns={[
            {
              key: "symbol",
              label: "Market",
              render: (m) => (
                <span>
                  <strong>{m.symbol}</strong>{" "}
                  <span className="muted small">{MARKET_LABEL[m.marketType]}</span>
                </span>
              ),
              sortValue: (m) => `${m.symbol} ${m.marketType}`,
              firstDirection: "asc",
            },
            {
              key: "health",
              label: "Health",
              render: (m) =>
                m.rows.some((d) => syncing.has(`${d.marketType}|${d.symbol}|${d.timeframe}`)) ? (
                  <HealthDot health="warn" label="Syncing…" />
                ) : (
                  <HealthDot
                    health={worst(m.rows)}
                    label={
                      worst(m.rows) === "warn" && m.rows.every((d) => d.candleCount === 0)
                        ? "No bars yet"
                        : { ok: "OK", warn: "Behind", error: "Failing", off: "Paused" }[worst(m.rows)]
                    }
                  />
                ),
              sortValue: (m) => ({ error: 0, warn: 1, ok: 2, off: 3 })[worst(m.rows)],
              firstDirection: "asc",
            },
            {
              key: "tfs",
              label: "Timeframes",
              render: (m) => (
                <span className="tf-chips">
                  {m.rows.map((d) => {
                    const h = healthOf(d);
                    const busy = syncing.has(`${d.marketType}|${d.symbol}|${d.timeframe}`);
                    return (
                      <span
                        key={d.timeframe}
                        className={`tf-chip tf-chip--${busy ? "busy" : h}`}
                        title={`${d.timeframe}: ${busy ? "syncing" : { ok: "current", warn: "behind", error: d.lastError ?? "failing", off: "paused" }[h]}`}
                      >
                        {d.timeframe}
                      </span>
                    );
                  })}
                </span>
              ),
              sortValue: (m) => m.rows.length,
            },
            {
              key: "bars",
              label: "Bars",
              align: "right",
              render: (m) => compact(m.rows.reduce((s, d) => s + d.candleCount, 0)),
              sortValue: (m) => m.rows.reduce((s, d) => s + d.candleCount, 0),
            },
            {
              key: "newest",
              label: "Newest bar",
              align: "right",
              render: (m) => timeAgo(Math.max(0, ...m.rows.map((d) => d.maxTime ?? 0)) || null),
              sortValue: (m) => Math.max(0, ...m.rows.map((d) => d.maxTime ?? 0)),
            },
            {
              key: "bots",
              label: "Bots",
              align: "right",
              title: "Bots running on this market (all bots in brackets)",
              render: (m) => {
                const r = m.rows.reduce((s, d) => s + d.botsRunning, 0);
                const t = m.rows.reduce((s, d) => s + d.botsTotal, 0);
                return t ? `${r} (${t})` : <span className="muted">0</span>;
              },
              sortValue: (m) => m.rows.reduce((s, d) => s + d.botsTotal, 0),
            },
            {
              key: "manage",
              label: <span className="sr-only">Manage</span>,
              render: (m) => (
                <button
                  type="button"
                  className={`btn btn--sm${open === m.key ? " is-on" : ""}`}
                  aria-expanded={open === m.key}
                  aria-label={`Manage ${m.symbol} ${MARKET_LABEL[m.marketType]}`}
                  onClick={() => setOpen(open === m.key ? null : m.key)}
                >
                  Manage
                </button>
              ),
            },
          ]}
        />
      </Card>

      <SyncJobs jobs={q.data?.jobs ?? []} loading={q.isPending} />
    </div>
  );
}

// --- adding markets -------------------------------------------------------------

interface SymbolsResponse {
  symbols: string[];
  groups: Array<{ label: string; symbols: string[] }>;
  popular: Array<{ symbol: string; quoteVolume: number }>;
  hosted: string[];
}

interface AddResult {
  added: Array<{ symbol: string; timeframe: string }>;
  skipped: Array<{ symbol: string; timeframe: string }>;
  invalid: Array<{ symbol: string; reason: string }>;
}

function AddMarkets({ data }: { data: DatasetsResponse | undefined }) {
  const qc = useQueryClient();
  const [marketType, setMarketType] = useState<MarketType>("spot");
  const [symbols, setSymbols] = useState<string[]>([]);
  const [draft, setDraft] = useState("");
  const [tfs, setTfs] = useState<string[]>(["15m", "1h", "4h", "1d"]);
  const [customDays, setCustomDays] = useState("");
  const [result, setResult] = useState<AddResult | null>(null);
  const daily = marketType === "equity" || marketType === "forex";
  const chosenTfs = daily ? ["1d"] : tfs;

  const list = useQuery({
    queryKey: ["admin", "dataset-symbols", marketType],
    queryFn: () => api<SymbolsResponse>(`/admin/datasets/symbols?marketType=${marketType}`),
    staleTime: 5 * 60_000,
  });
  const known = useMemo(() => new Set(list.data?.symbols ?? []), [list.data]);

  /** "sol", "SOL/USDT" and "SOLUSDT" all mean SOLUSDT on Binance. */
  const normalize = (raw: string): string => {
    const s = raw.trim().toUpperCase();
    if (daily) return s;
    const pair = s.replace(/[/\s-]/g, "");
    if (!known.size || known.has(pair)) return pair;
    return known.has(`${pair}USDT`) ? `${pair}USDT` : pair;
  };
  const addSymbols = (raw: string) => {
    const next = raw
      .split(/[\s,;]+/)
      .map(normalize)
      .filter(Boolean);
    if (next.length) setSymbols((cur) => [...new Set([...cur, ...next])].slice(0, 20));
    setDraft("");
  };
  const onKey = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" || e.key === "," || e.key === " ") {
      e.preventDefault();
      addSymbols(draft);
    } else if (e.key === "Backspace" && !draft && symbols.length) {
      setSymbols(symbols.slice(0, -1));
    }
  };

  const add = useMutation({
    mutationFn: () =>
      api<AddResult>("/admin/datasets", {
        method: "POST",
        body: {
          marketType,
          symbols: draft.trim()
            ? [
                ...symbols,
                ...draft
                  .split(/[\s,;]+/)
                  .map(normalize)
                  .filter(Boolean),
              ]
            : symbols,
          timeframes: chosenTfs,
          historyDays: customDays ? Number(customDays) : null,
        },
      }),
    onSuccess: (r) => {
      setResult(r);
      setSymbols([]);
      setDraft("");
      void qc.invalidateQueries({ queryKey: ["admin", "datasets"] });
      void qc.invalidateQueries({ queryKey: ["admin", "dataset-symbols"] });
      void qc.invalidateQueries({ queryKey: ["admin", "alerts"] });
    },
  });

  const count = (symbols.length + (draft.trim() ? 1 : 0)) * chosenTfs.length;
  const hosted = new Set(list.data?.hosted ?? []);
  const suggestions = [
    ...(list.data?.popular.length
      ? [{ label: "Most traded, not hosted", symbols: list.data.popular.map((p) => p.symbol) }]
      : []),
    ...(list.data?.groups ?? []),
  ];
  const volume = new Map((list.data?.popular ?? []).map((p) => [p.symbol, p.quoteVolume]));

  return (
    <Card title="Add markets">
      <div className="stack">
        <div className="field">
          <span className="field__label" id="add-venue">
            Venue
          </span>
          <div className="segmented" role="group" aria-labelledby="add-venue">
            {(Object.keys(MARKET_LABEL) as MarketType[]).map((t) => (
              <button
                key={t}
                type="button"
                className={marketType === t ? "is-active" : undefined}
                aria-pressed={marketType === t}
                onClick={() => {
                  setMarketType(t);
                  setSymbols([]);
                  setResult(null);
                }}
              >
                {MARKET_LABEL[t]}
              </button>
            ))}
          </div>
          <span className="muted small">
            {marketType === "spot"
              ? "Binance spot pairs. Every timeframe."
              : marketType === "futures"
                ? "Binance USD-M perpetuals, with their funding rates. Every timeframe; leveraged bots run here."
                : marketType === "equity"
                  ? "US stocks and ETFs, daily bars only (Yahoo). Use the ticker: AAPL, SPY."
                  : "Currency pairs, daily bars only (Yahoo). Write the pair: EURUSD."}
          </span>
        </div>

        <div className="field">
          <label className="field__label" htmlFor="add-symbols">
            Symbols
          </label>
          <div className="symbol-input">
            {symbols.map((s) => (
              <span key={s} className={`chip${!daily && known.size && !known.has(s) ? " chip--fail" : ""}`}>
                {s}
                {hosted.has(s) && <span className="muted small"> hosted</span>}
                <button
                  type="button"
                  className="chip__btn"
                  aria-label={`Remove ${s}`}
                  onClick={() => setSymbols(symbols.filter((x) => x !== s))}
                >
                  ×
                </button>
              </span>
            ))}
            <input
              id="add-symbols"
              list={daily ? undefined : "add-symbols-list"}
              value={draft}
              placeholder={symbols.length ? "" : daily ? "AAPL, MSFT…" : "SUI, PEPEUSDT…"}
              autoComplete="off"
              onChange={(e) => {
                const v = e.target.value;
                // Picking from the list or pasting "A, B, C" adds them at once.
                if (/[\s,;]/.test(v) || known.has(v.toUpperCase())) addSymbols(v);
                else setDraft(v);
              }}
              onKeyDown={onKey}
              onBlur={() => draft.trim() && addSymbols(draft)}
            />
          </div>
          {!daily && (
            <datalist id="add-symbols-list">
              {(list.data?.symbols ?? [])
                .filter((s) => !draft || s.startsWith(draft.toUpperCase()))
                .slice(0, 200)
                .map((s) => (
                  <option key={s} value={s} />
                ))}
            </datalist>
          )}
          <span className="muted small">
            Type and press Enter, or paste a list. {daily ? "" : "SOL becomes SOLUSDT. "}Up to 20 at a time.
            Each is checked with its source before it is added.
          </span>
        </div>

        {suggestions.length > 0 && (
          <div className="field">
            <span className="field__label">Suggestions (click to add)</span>
            {suggestions.map((g) => (
              <div key={g.label} className="suggest-row">
                <span className="muted small suggest-row__label">{g.label}</span>
                <span className="tf-chips">
                  {g.symbols.slice(0, 16).map((s) => (
                    <button
                      key={s}
                      type="button"
                      className="toggle-chip"
                      aria-pressed={symbols.includes(s)}
                      title={volume.has(s) ? `24h volume ${compact(volume.get(s)!)} USDT` : undefined}
                      onClick={() =>
                        setSymbols(
                          symbols.includes(s) ? symbols.filter((x) => x !== s) : [...symbols, s].slice(0, 20),
                        )
                      }
                    >
                      {s}
                    </button>
                  ))}
                </span>
              </div>
            ))}
          </div>
        )}

        <div className="field">
          <span className="field__label" id="add-tfs">
            Timeframes
          </span>
          {daily ? (
            <span className="muted small">Daily (1d): the only one this source serves.</span>
          ) : (
            <>
              <div className="row">
                <div className="segmented" role="group" aria-label="Timeframe presets">
                  {PRESETS.map((p) => {
                    const on = p.tfs.length === tfs.length && p.tfs.every((t) => tfs.includes(t));
                    return (
                      <button
                        key={p.label}
                        type="button"
                        className={on ? "is-active" : undefined}
                        aria-pressed={on}
                        onClick={() => setTfs(p.tfs)}
                      >
                        {p.label}
                      </button>
                    );
                  })}
                </div>
              </div>
              <span className="tf-chips" role="group" aria-labelledby="add-tfs">
                {ALL_TFS.map((t) => (
                  <button
                    key={t}
                    type="button"
                    className="toggle-chip"
                    aria-pressed={tfs.includes(t)}
                    onClick={() =>
                      setTfs(
                        tfs.includes(t)
                          ? tfs.filter((x) => x !== t)
                          : [...tfs, t].sort((a, b) => tfOrder(a) - tfOrder(b)),
                      )
                    }
                  >
                    {t}
                  </button>
                ))}
              </span>
            </>
          )}
        </div>

        <label className="field ops-days">
          <span className="field__label">History to keep (days)</span>
          <input
            type="number"
            min={1}
            max={3650}
            value={customDays}
            placeholder="Default per timeframe"
            onChange={(e) => setCustomDays(e.target.value)}
          />
          <span className="muted small">
            Empty uses the default, sized to roughly 5 to 25 thousand bars:{" "}
            {chosenTfs
              .slice(0, 6)
              .map((t) => `${t} ${data?.defaults[t] ?? "?"}`)
              .join(", ")}
            {chosenTfs.length > 6 ? "…" : ""}.
          </span>
        </label>

        {add.error && <Alert tone="error">{add.error.message}</Alert>}
        {result && <AddOutcome result={result} />}

        <div className="row">
          <button
            type="button"
            className="btn btn--primary"
            disabled={!count || add.isPending}
            onClick={() => {
              setResult(null);
              add.mutate();
            }}
          >
            {add.isPending ? "Checking and adding…" : `Add ${count || ""} dataset${count === 1 ? "" : "s"}`}
          </button>
          <span className="muted small">
            {count
              ? `${symbols.length + (draft.trim() ? 1 : 0)} symbol(s) × ${chosenTfs.length} timeframe(s). The first sync starts right away.`
              : "Pick at least one symbol and timeframe."}
          </span>
        </div>
      </div>
    </Card>
  );
}

function AddOutcome({ result }: { result: AddResult }) {
  const by = (rows: Array<{ symbol: string; timeframe: string }>) => {
    const m = new Map<string, string[]>();
    for (const r of rows) m.set(r.symbol, [...(m.get(r.symbol) ?? []), r.timeframe]);
    return [...m.entries()].map(([s, t]) => `${s} (${t.join(", ")})`).join("; ");
  };
  return (
    <div className="stack-tight">
      {result.added.length > 0 && (
        <Alert tone="ok">
          Added {result.added.length}: {by(result.added)}. Syncing now; watch the jobs below.
        </Alert>
      )}
      {result.skipped.length > 0 && (
        <p className="muted small">Already hosted, left as they were: {by(result.skipped)}.</p>
      )}
      {result.invalid.length > 0 && (
        <Alert tone="error">
          Not added: {result.invalid.map((i) => `${i.symbol} (${i.reason})`).join("; ")}.
        </Alert>
      )}
    </div>
  );
}

// --- one market ------------------------------------------------------------------

function MarketDetail({
  market,
  syncing,
  onClose,
}: {
  market: Market;
  syncing: Set<string>;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [purge, setPurge] = useState(false);
  const [days, setDays] = useState<Record<string, string>>({});
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ["admin", "datasets"] });
    void qc.invalidateQueries({ queryKey: ["admin", "alerts"] });
  };
  const base = { marketType: market.marketType, symbol: market.symbol };
  const sync = useMutation({
    mutationFn: (timeframes?: string[]) =>
      api("/admin/datasets/sync", {
        method: "POST",
        body: { ...base, ...(timeframes ? { timeframes } : {}) },
      }),
    onSuccess: refresh,
  });
  const update = useMutation({
    mutationFn: (body: { timeframes?: string[]; enabled?: boolean; historyDays?: number }) =>
      api("/admin/datasets", { method: "PATCH", body: { ...base, ...body } }),
    onSuccess: refresh,
  });
  const remove = useMutation({
    mutationFn: (body: { timeframes?: string[]; purge: boolean }) =>
      api<{ removed: number; candlesDeleted: number }>("/admin/datasets/remove", {
        method: "POST",
        body: { ...base, ...body },
      }),
    onSuccess: refresh,
  });
  const addTf = useMutation({
    mutationFn: (timeframe: string) =>
      api<AddResult>("/admin/datasets", {
        method: "POST",
        body: { marketType: market.marketType, symbols: [market.symbol], timeframes: [timeframe] },
      }),
    onSuccess: refresh,
  });
  const error = sync.error ?? update.error ?? remove.error ?? addTf.error;
  const daily = market.marketType === "equity" || market.marketType === "forex";
  const missing = daily ? [] : ALL_TFS.filter((t) => !market.rows.some((r) => r.timeframe === t));
  const anyOn = market.rows.some((r) => r.enabled);
  const botsTotal = market.rows.reduce((s, d) => s + d.botsTotal, 0);
  const busy = sync.isPending || update.isPending || remove.isPending || addTf.isPending;

  return (
    <Card
      title={
        <span>
          {market.symbol} <span className="muted small">{MARKET_LABEL[market.marketType]}</span>
        </span>
      }
      actions={
        <>
          <button
            type="button"
            className="btn btn--sm"
            disabled={busy}
            onClick={() => sync.mutate(undefined)}
          >
            Sync all now
          </button>
          <button
            type="button"
            className="btn btn--sm"
            disabled={busy}
            onClick={() => update.mutate({ enabled: !anyOn })}
          >
            {anyOn ? "Pause all" : "Resume all"}
          </button>
          <button type="button" className="btn btn--sm btn--ghost" onClick={onClose}>
            Close
          </button>
        </>
      }
      flush
    >
      {error && <Alert tone="error">{error.message}</Alert>}
      {remove.data && (
        <Alert tone="ok">
          Removed {remove.data.removed} dataset{remove.data.removed === 1 ? "" : "s"}
          {remove.data.candlesDeleted ? ` and ${remove.data.candlesDeleted.toLocaleString()} candles` : ""}.
        </Alert>
      )}
      <div className="table-wrap">
        <table>
          <caption className="sr-only">{market.symbol} by timeframe</caption>
          <thead>
            <tr>
              <th>Timeframe</th>
              <th>Status</th>
              <th className="num">Bars</th>
              <th className="num">From</th>
              <th className="num">Newest</th>
              <th className="num">History (days)</th>
              <th className="num">Last sync</th>
              <th className="num">Bots</th>
              <th>
                <span className="sr-only">Actions</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {market.rows.map((d) => {
              const h = healthOf(d);
              const isSyncing = syncing.has(`${d.marketType}|${d.symbol}|${d.timeframe}`);
              const v = days[d.timeframe] ?? String(d.historyDays);
              return (
                <tr key={d.timeframe}>
                  <td className="mono">{d.timeframe}</td>
                  <td>
                    {isSyncing ? (
                      <HealthDot health="warn" label="Syncing…" />
                    ) : (
                      <span title={d.lastError ?? undefined}>
                        <HealthDot
                          health={h}
                          label={
                            {
                              ok: "Current",
                              warn: d.candleCount ? "Behind" : "No bars yet",
                              error: "Sync failed",
                              off: "Paused",
                            }[h]
                          }
                        />
                      </span>
                    )}
                    {d.lastError && <div className="down small ops-error">{d.lastError}</div>}
                  </td>
                  <td className="num mono">{d.candleCount.toLocaleString()}</td>
                  <td className="num small">{date(d.minTime)}</td>
                  <td className="num small">{timeAgo(d.maxTime)}</td>
                  <td className="num">
                    <input
                      className="ops-days-input"
                      type="number"
                      min={1}
                      max={3650}
                      aria-label={`History in days for ${d.timeframe}`}
                      value={v}
                      onChange={(e) => setDays({ ...days, [d.timeframe]: e.target.value })}
                      onBlur={() => {
                        const n = Number(v);
                        if (Number.isInteger(n) && n > 0 && n !== d.historyDays)
                          update.mutate({ timeframes: [d.timeframe], historyDays: n });
                      }}
                      onKeyDown={(e) => e.key === "Enter" && (e.target as HTMLInputElement).blur()}
                    />
                  </td>
                  <td className="num small">
                    {timeAgo(d.lastSyncAt)}
                    {d.lastSyncMs != null && (
                      <span className="muted"> · {(d.lastSyncMs / 1000).toFixed(1)}s</span>
                    )}
                  </td>
                  <td className="num">
                    {d.botsTotal ? `${d.botsRunning} (${d.botsTotal})` : <span className="muted">0</span>}
                  </td>
                  <td className="nowrap">
                    <button
                      type="button"
                      className="btn btn--sm"
                      disabled={busy || isSyncing}
                      aria-label={`Sync ${d.timeframe} now`}
                      onClick={() => sync.mutate([d.timeframe])}
                    >
                      Sync
                    </button>{" "}
                    <button
                      type="button"
                      className="btn btn--sm"
                      disabled={busy}
                      aria-label={`${d.enabled ? "Pause" : "Resume"} ${d.timeframe}`}
                      onClick={() => update.mutate({ timeframes: [d.timeframe], enabled: !d.enabled })}
                    >
                      {d.enabled ? "Pause" : "Resume"}
                    </button>{" "}
                    <button
                      type="button"
                      className="btn btn--sm btn--danger"
                      disabled={busy}
                      aria-label={`Remove ${d.timeframe}`}
                      onClick={() => {
                        if (
                          window.confirm(
                            `Stop hosting ${market.symbol} ${d.timeframe}? Its candles are kept.`,
                          )
                        )
                          remove.mutate({ timeframes: [d.timeframe], purge: false });
                      }}
                    >
                      Remove
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="card__body stack">
        {missing.length > 0 && (
          <div className="field">
            <span className="field__label">Add a timeframe</span>
            <span className="tf-chips">
              {missing.map((t) => (
                <button
                  key={t}
                  type="button"
                  className="toggle-chip"
                  aria-pressed={false}
                  aria-label={`Add ${t}`}
                  disabled={busy}
                  onClick={() => addTf.mutate(t)}
                >
                  + {t}
                </button>
              ))}
            </span>
          </div>
        )}
        <p className="muted small">
          Paused: not synced, and hidden from members&rsquo; market pickers; bots already on it keep running
          on their live feed. Removing does the same and drops it from this list; adding it again brings it
          back.
        </p>
        <div className="row ops-danger">
          <label className="checkbox">
            <input
              type="checkbox"
              checked={purge}
              disabled={botsTotal > 0}
              onChange={(e) => setPurge(e.target.checked)}
            />{" "}
            Also delete its stored candles
            {botsTotal > 0 && <span className="muted small"> (not while {botsTotal} bot(s) use it)</span>}
          </label>
          <button
            type="button"
            className="btn btn--sm btn--danger"
            disabled={busy}
            onClick={() => {
              if (
                window.confirm(
                  `Remove ${market.symbol} ${MARKET_LABEL[market.marketType]} on every timeframe?${
                    purge ? " Its candles are deleted too; this can't be undone." : " Its candles are kept."
                  }`,
                )
              ) {
                remove.mutate({ purge }, { onSuccess: onClose });
              }
            }}
          >
            Remove market
          </button>
        </div>
      </div>
    </Card>
  );
}

// --- the data queue ------------------------------------------------------------------

function jobLabel(j: DataJobRow): string {
  switch (j.data.kind) {
    case "sync-all":
      return "Sync every market";
    case "sync":
      return `Sync ${j.data.symbol} ${j.data.timeframe} ${j.data.marketType}`;
    case "league-score":
      return "League scoring";
    case "prune-events":
      return "Prune old bot logs";
    default:
      return j.name;
  }
}

function SyncJobs({ jobs, loading }: { jobs: DataJobRow[]; loading: boolean }) {
  const qc = useQueryClient();
  const all = useMutation({
    mutationFn: () => api("/admin/datasets/sync", { method: "POST", body: {} }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", "datasets"] }),
  });
  const running = jobs.some(
    (j) => j.data.kind === "sync-all" && (j.state === "active" || j.state === "waiting"),
  );
  return (
    <Card
      title="Sync jobs"
      actions={
        <button
          type="button"
          className="btn btn--sm"
          disabled={running || all.isPending}
          onClick={() => all.mutate()}
        >
          {running ? "Syncing everything…" : "Sync everything now"}
        </button>
      }
      flush
    >
      {all.error && <Alert tone="error">{all.error.message}</Alert>}
      <SortableTable
        rows={jobs}
        loading={loading}
        rowKey={(j) => j.id}
        caption="Recent data jobs"
        empty={<p className="card__body muted">No jobs yet.</p>}
        columns={[
          {
            key: "job",
            label: "Job",
            render: (j) => jobLabel(j),
            sortValue: (j) => jobLabel(j),
            firstDirection: "asc",
          },
          {
            key: "state",
            label: "State",
            render: (j) => (
              <span
                className={`badge${j.state === "failed" ? " badge--danger" : j.state === "completed" ? " badge--success" : j.state === "active" ? " badge--accent" : ""}`}
              >
                {j.state}
              </span>
            ),
            sortValue: (j) => j.state,
            firstDirection: "asc",
          },
          {
            key: "progress",
            label: "Progress",
            render: (j) => {
              const p = typeof j.progress === "object" && j.progress ? j.progress : null;
              if (!p) return <span className="muted">—</span>;
              return (
                <span className="ops-meter">
                  <span className="ops-meter__track" aria-hidden="true">
                    <span
                      className="ops-meter__fill"
                      style={{ width: `${(p.done / Math.max(1, p.total)) * 100}%` }}
                    />
                  </span>
                  <span className="mono small">
                    {p.done}/{p.total}
                  </span>
                </span>
              );
            },
          },
          {
            key: "added",
            label: "Started",
            align: "right",
            render: (j) => timeAgo(j.startedAt ?? j.addedAt),
            sortValue: (j) => j.addedAt,
          },
          {
            key: "took",
            label: "Took",
            align: "right",
            render: (j) =>
              j.finishedAt && j.startedAt ? `${((j.finishedAt - j.startedAt) / 1000).toFixed(1)}s` : "—",
            sortValue: (j) => (j.finishedAt && j.startedAt ? j.finishedAt - j.startedAt : null),
          },
          {
            key: "result",
            label: "Result",
            render: (j) =>
              j.error ? (
                <span className="down small">{j.error}</span>
              ) : j.synced != null ? (
                <span className="small">
                  {j.synced} synced{j.failed ? <span className="down">, {j.failed} failed</span> : null}
                </span>
              ) : (
                <span className="muted">—</span>
              ),
          },
        ]}
      />
    </Card>
  );
}
